import { useRef, useCallback, useEffect, useState } from 'react'
import {
  musicLoader,
  musicGameplay,
  sfxLand,
  sfxClick,
  sfxWin,
} from '@/assets/audio'

// Типы звуков
type MusicType = 'loader' | 'gameplay'
type SfxType = 'land' | 'click' | 'win'

// Маппинг звуков
const MUSIC_MAP: Record<MusicType, string> = {
  loader: musicLoader,
  gameplay: musicGameplay,
}

const SFX_MAP: Record<SfxType, string> = {
  land: sfxLand,
  click: sfxClick,
  win: sfxWin,
}

// Громкость по умолчанию
const SFX_VOLUMES: Record<SfxType, number> = {
  land: 0.5,    // 50% громкости
  click: 0.7,
  win: 0.8,
}

function handlePlaybackError(error: unknown): void {
  if (error instanceof DOMException && error.name === 'NotAllowedError') return
  console.warn('[Audio] Playback failed:', error)
}

export function useAudio() {
  const musicRef = useRef<HTMLAudioElement | null>(null)
  const currentMusicRef = useRef<MusicType | null>(null)
  
  // Состояние музыки и звуков (раздельно)
  const [isMusicEnabled, setIsMusicEnabled] = useState(false)
  const [isSoundEnabled, setIsSoundEnabled] = useState(true)
  
  // Refs для использования в callbacks
  const isMusicEnabledRef = useRef(false)
  const isSoundEnabledRef = useRef(true)
  
  useEffect(() => {
    isSoundEnabledRef.current = isSoundEnabled
  }, [isSoundEnabled])
  
  // === МУЗЫКА ===
  const playMusic = useCallback((type: MusicType) => {
    if (!isMusicEnabledRef.current) {
      currentMusicRef.current = type
      return
    }
    
    // Если уже играет эта музыка — не перезапускаем
    if (currentMusicRef.current === type && musicRef.current && !musicRef.current.paused) {
      return
    }
    
    // Останавливаем предыдущую
    if (musicRef.current) {
      musicRef.current.pause()
      musicRef.current = null
    }
    
    // Создаём новый аудио элемент
    const audio = new Audio(MUSIC_MAP[type])
    audio.loop = true
    audio.volume = 0.4 // 40% громкости для музыки
    audio.play().catch(handlePlaybackError)
    
    musicRef.current = audio
    currentMusicRef.current = type
  }, [])

  useEffect(() => {
    isMusicEnabledRef.current = isMusicEnabled

    if (isMusicEnabled) {
      if (musicRef.current) {
        musicRef.current.play().catch(handlePlaybackError)
      } else if (currentMusicRef.current) {
        playMusic(currentMusicRef.current)
      }
    } else {
      musicRef.current?.pause()
    }
  }, [isMusicEnabled, playMusic])
  
  const stopMusic = useCallback(() => {
    if (musicRef.current) {
      musicRef.current.pause()
      musicRef.current = null
      currentMusicRef.current = null
    }
  }, [])
  
  const fadeToMusic = useCallback((type: MusicType, duration = 1000) => {
    if (!isMusicEnabledRef.current) {
      // Просто запоминаем какую музыку нужно играть
      currentMusicRef.current = type
      return
    }
    
    const oldAudio = musicRef.current
    
    // Создаём новую музыку
    const newAudio = new Audio(MUSIC_MAP[type])
    newAudio.loop = true
    newAudio.volume = 0
    newAudio.play().catch(handlePlaybackError)
    
    musicRef.current = newAudio
    currentMusicRef.current = type
    
    // Плавный переход
    const steps = 20
    const stepDuration = duration / steps
    const volumeStep = 0.4 / steps
    
    let step = 0
    const interval = setInterval(() => {
      step++
      
      // Затухание старой
      if (oldAudio) {
        oldAudio.volume = Math.max(0, 0.4 - volumeStep * step)
      }
      
      // Нарастание новой
      newAudio.volume = Math.min(0.4, volumeStep * step)
      
      if (step >= steps) {
        clearInterval(interval)
        if (oldAudio) {
          oldAudio.pause()
        }
      }
    }, stepDuration)
  }, [])
  
  // === ЗВУКОВЫЕ ЭФФЕКТЫ ===
  const playSfx = useCallback((type: SfxType) => {
    if (!isSoundEnabledRef.current) return
    
    const audio = new Audio(SFX_MAP[type])
    audio.volume = SFX_VOLUMES[type]
    audio.play().catch(handlePlaybackError)
  }, [])
  
  // === ХАПТИКИ (вибрация) ===
  const vibrate = useCallback((pattern: number | number[] = 50) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern)
    }
  }, [])
  
  // Короткий толчок при приземлении
  const vibrateOnLand = useCallback(() => {
    vibrate(30) // 30ms короткий толчок
  }, [vibrate])
  
  // === TOGGLE ФУНКЦИИ ===
  const toggleMusic = useCallback(() => {
    setIsMusicEnabled(prev => !prev)
  }, [])
  
  const toggleSound = useCallback(() => {
    setIsSoundEnabled(prev => !prev)
  }, [])
  
  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (musicRef.current) {
        musicRef.current.pause()
        musicRef.current = null
      }
    }
  }, [])
  
  return {
    // Музыка
    playMusic,
    stopMusic,
    fadeToMusic,
    isMusicEnabled,
    toggleMusic,
    
    // Звуки
    playSfx,
    isSoundEnabled,
    toggleSound,
    
    // Хаптики
    vibrate,
    vibrateOnLand,
  }
}

